import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  List,
  Grid,
  ChevronLeft,
  ChevronRight,
  X,
  Copy,
  ExternalLink,
  Info,
} from 'lucide-react';
import EventTooltip from '../components/EventTooltip';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
} from '../utils/animations';

interface CalendarEvent {
  id: string;
  summary: string;
  conferenceData?: {
    entryPoints?: Array<{
      entryPointType: string;
      uri: string;
    }>;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  description?: string;
  location?: string;
}

interface DayViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date | null;
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}

function DayViewModal({
  isOpen,
  onClose,
  date,
  events,
  onEventClick,
}: DayViewModalProps) {
  if (!isOpen || !date) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {date.toLocaleDateString('ro-RO', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {events.length > 0 ? (
            <div className="space-y-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className={`p-4 rounded-lg transition-colors ${
                    event.conferenceData?.entryPoints
                      ? 'bg-green-50 hover:bg-green-100 cursor-pointer'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {event.summary}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(event.start.dateTime).toLocaleTimeString(
                          'ro-RO',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </p>
                      {event.description && (
                        <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                          {event.description}
                        </p>
                      )}
                      {event.location && (
                        <div className="flex items-center mt-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                    {event.conferenceData?.entryPoints && (
                      <span className="text-sm text-green-600 font-medium">
                        Consultație online
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 py-8">
              Nu există programări pentru această zi
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Appointments() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDayView, setShowDayView] = useState(false);

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const startOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getMonthDays = () => {
    const start = startOfMonth(currentDate);
    const days = daysInMonth(currentDate);
    const startDay = start.getDay();
    const result = [];

    // Add empty cells for days before first of the month
    for (let i = 0; i < startDay; i++) {
      result.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= days; i++) {
      result.push(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), i)
      );
    }

    return result;
  };

  const getEventsForDay = (date: Date) => {
    return events.filter((event) => {
      const eventDate = new Date(event.start.dateTime);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Helper functions for week grouping
  const groupEventsByWeek = (events: CalendarEvent[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    
    const groups: { [key: string]: CalendarEvent[] } = {};
    
    events.forEach(event => {
      const eventDate = new Date(event.start.dateTime);
      const daysDiff = Math.floor((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      let weekKey;
      if (daysDiff < 7) {
        weekKey = 'current';
      } else if (daysDiff < 14) {
        weekKey = 'next';
      } else {
        weekKey = `week-${Math.floor(daysDiff / 7)}`;
      }
      
      if (!groups[weekKey]) {
        groups[weekKey] = [];
      }
      groups[weekKey].push(event);
    });
    
    return groups;
  };

  const getWeekHeader = (weekKey: string) => {
    switch (weekKey) {
      case 'current':
        return 'Evenimente de săptămâna aceasta';
      case 'next':
        return 'Evenimente de săptămâna viitoare';
      default:
        return `Evenimente săptămâna ${weekKey}`;
    }
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setShowDayView(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    const meetLink = getMeetLink(event);
    if (meetLink) {
      window.open(meetLink, '_blank');
    }
  };

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  useEffect(() => {
    checkAuth();
    fetchCalendarEvents();
  }, [navigate, viewMode]);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (!profile || profile.role !== 'doctor') {
        navigate('/');
        return;
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      navigate('/login');
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const calendarId = encodeURIComponent('ovidiu@decizieoncologica.ro');
      const apiKey = 'AIzaSyD_U7lB4IcAg1dG4oM8fFc1JfBe_Lcuz88';
      const timeMin = new Date().toISOString();
      // Use different time ranges based on view mode
      const daysAhead = viewMode === 'list' ? 10 : 30;
      const timeMax = new Date(
        Date.now() + daysAhead * 24 * 60 * 60 * 1000
      ).toISOString();

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?` +
          `key=${apiKey}&timeMin=${timeMin}&timeMax=${timeMax}&` +
          'singleEvents=true&orderBy=startTime&conferenceDataVersion=1'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch calendar events');
      }

      const data = await response.json();
      setEvents(data.items);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load calendar events'
      );
      console.error('Error fetching calendar events:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('ro-RO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMeetLink = (event: CalendarEvent) => {
    if (!event.conferenceData?.entryPoints) return null;
    return event.conferenceData.entryPoints.find(
      (ep) => ep.entryPointType === 'video'
    )?.uri;
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-8 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white rounded-lg shadow-lg p-6"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div
            className="flex justify-between items-center mb-6"
            variants={fadeInUp}
          >
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-[#007FFF]" />
              <h1 className="text-3xl font-bold text-gray-900">
                Programări pacienți
              </h1>
            </div>
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
                  viewMode === 'list'
                    ? 'bg-white shadow text-[#007FFF]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Listă</span>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
                  viewMode === 'calendar'
                    ? 'bg-white shadow text-[#007FFF]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid className="w-4 h-4" />
                <span className="hidden sm:inline">Calendar</span>
              </button>
            </div>
          </motion.div>

          {loading ? (
            <motion.div
              className="text-center py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007FFF] mx-auto"></div>
              <p className="mt-4 text-gray-600">Se încarcă programările...</p>
            </motion.div>
          ) : error ? (
            <motion.div
              className="bg-red-50 border-l-4 border-red-500 p-4 mb-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-red-700">{error}</p>
            </motion.div>
          ) : events.length === 0 ? (
            <motion.div className="text-center py-8" variants={fadeInUp}>
              <p className="text-gray-600">
                Nu există programări în următoarele {viewMode === 'list' ? '10' : '30'} de zile
              </p>
            </motion.div>
          ) : (
            <>
              {/* Informational banner for list view */}
              {viewMode === 'list' && (
                <motion.div
                  className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 flex items-start gap-3"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  variants={fadeInUp}
                >
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 text-sm">
                      Aici puteți vedea programările pentru următoarele 10 zile. Pentru a le verifica pe o perioadă mai lungă, faceți clic pe calendar
                    </p>
                    <button
                      onClick={() => setViewMode('calendar')}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium underline transition-colors"
                    >
                      Vizualizare calendar
                    </button>
                  </div>
                </motion.div>
              )}

              {viewMode === 'calendar' ? (
                <motion.div
                  className="bg-white rounded-lg shadow"
                  initial="initial"
                  animate="animate"
                  variants={staggerContainer}
                >
                  <div className="flex items-center justify-between p-4 border-b">
                    <button
                      onClick={previousMonth}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-semibold">
                      {currentDate.toLocaleString('ro-RO', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </h2>
                    <button
                      onClick={nextMonth}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-px bg-gray-200">
                    {[
                      'Duminică',
                      'Luni',
                      'Marți',
                      'Miercuri',
                      'Joi',
                      'Vineri',
                      'Sâmbătă',
                    ].map((day) => (
                      <div
                        key={day}
                        className="bg-gray-50 py-2 text-center text-sm font-medium"
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <motion.div
                    className="grid grid-cols-7 gap-px bg-gray-200"
                    variants={fastStaggerContainer}
                  >
                    {getMonthDays().map((date, index) => (
                      <motion.div
                        key={index}
                        className={`bg-white min-h-[100px] p-2 ${
                          date ? 'relative hover:bg-gray-50 cursor-pointer' : ''
                        }`}
                        onClick={() => date && handleDayClick(date)}
                        variants={scaleIn}
                        whileHover={{ scale: 1.02 }}
                      >
                        {date && (
                          <>
                            <div
                              className={`text-sm ${
                                date.getDate() === new Date().getDate() &&
                                date.getMonth() === new Date().getMonth() &&
                                date.getFullYear() === new Date().getFullYear()
                                  ? 'bg-[#007FFF] text-white w-6 h-6 rounded-full flex items-center justify-center'
                                  : ''
                              }`}
                            >
                              {date.getDate()}
                            </div>
                            <div className="mt-1 space-y-1">
                              {getEventsForDay(date).map((event) => (
                                <EventTooltip
                                  key={event.id}
                                  title={`${event.summary}${
                                    event.description
                                      ? '\n\n' + event.description
                                      : ''
                                  }`}
                                >
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEventClick(event);
                                    }}
                                    className={`
                                      text-xs p-1 rounded
                                      ${
                                        getMeetLink(event)
                                          ? 'bg-green-100 text-green-800 cursor-pointer hover:bg-green-200'
                                          : 'bg-blue-100 text-blue-800'
                                      }
                                    `}
                                  >
                                    <div className="truncate">
                                      {new Date(
                                        event.start.dateTime
                                      ).toLocaleTimeString('ro-RO', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                      - {event.summary}
                                    </div>
                                  </div>
                                </EventTooltip>
                              ))}
                            </div>
                          </>
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  className="space-y-6"
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true, margin: '-50px' }}
                  variants={fastStaggerContainer}
                >
                  {Object.entries(groupEventsByWeek(events)).map(([weekKey, weekEvents], weekIndex) => (
                    <div key={weekKey} className="space-y-4">
                      <motion.div
                        className="flex items-center gap-2 mb-4"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: weekIndex * 0.1 }}
                        variants={fadeInUp}
                      >
                        <h3 className="text-lg font-semibold text-gray-800 border-l-4 border-blue-500 pl-3 py-1">
                          {getWeekHeader(weekKey)}
                        </h3>
                      </motion.div>
                      <div className="space-y-4">
                        {weekEvents.map((event) => (
                          <motion.div
                            onClick={() => {
                              const meetLink = getMeetLink(event);
                              if (meetLink) {
                                window.open(meetLink, '_blank');
                              }
                            }}
                            key={event.id}
                            className={`bg-gray-50 rounded-lg p-4 shadow transition ${
                              getMeetLink(event)
                                ? 'hover:shadow-md hover:bg-gray-100 cursor-pointer'
                                : ''
                            }`}
                            variants={scaleIn}
                            whileHover={{ y: -3, transition: { duration: 0.3 } }}
                          >
                            <div className="flex items-start gap-4">
                              <div className="bg-[#007FFF] rounded-lg p-3 text-white">
                                <Calendar className="w-6 h-6" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {event.summary}
                                </h3>
                                <div className="mt-2 space-y-2">
                                  <div className="flex items-center text-gray-600">
                                    <Clock className="w-4 h-4 mr-2" />
                                    <span>{formatDateTime(event.start.dateTime)}</span>
                                  </div>
                                  {event.location && (
                                    <div className="flex items-center text-gray-600">
                                      <MapPin className="w-4 h-4 mr-2" />
                                      <span>{event.location}</span>
                                    </div>
                                  )}
                                  {event.description && (
                                    <div className="flex items-start text-gray-600 mt-2">
                                      <User className="w-4 h-4 mr-2 mt-1" />
                                      <p className="whitespace-pre-wrap">
                                        {event.description}
                                      </p>
                                    </div>
                                  )}
                                  {getMeetLink(event) && (
                                    <div className="mt-3 space-y-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const meetLink = getMeetLink(event);
                                          if (meetLink) {
                                            window.open(meetLink, '_blank');
                                          }
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors w-full"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                        Click pentru a intra în consultație
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const meetLink = getMeetLink(event);
                                          if (meetLink) {
                                            navigator.clipboard.writeText(meetLink);
                                          }
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors w-full"
                                      >
                                        <Copy className="w-4 h-4" />
                                        Copiază link consultație
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        <DayViewModal
          isOpen={showDayView}
          onClose={() => setShowDayView(false)}
          date={selectedDate}
          events={selectedDate ? getEventsForDay(selectedDate) : []}
          onEventClick={handleEventClick}
        />
      </AnimatePresence>
    </div>
  );
}
